import Backbone from 'backbone'
import _ from 'lodash'
import applyFilter from 'scoreboard/datasets/apply-filter'
import treeTypeToDataType from 'scoreboard/components/tree-type-to-data-type'
import applyDrilldownActionFactory from 'scoreboard/dashboards/widgets/apply-drilldown-action-factory'
import layoutManager from 'scoreboard/components/layout-manager'

/**
 * Handles drilldown actions on a chart (currently tree-map charts), such as
 * listening for drilldown events, applying drilldown filters, etc
 */
class DrilldownManager {
	constructor ({ chartDesigner, chartViewObject, chartWidgetView }) {
		this.chartDesigner = chartDesigner
		this.chartViewObject = chartViewObject
		this.chartWidgetView = chartWidgetView

		this.addListeners()
	}

	addListeners () {
		this.listeners = {
			onSelectPoint: (point, range, opt) => this.onSelectPoint(point, range, opt),
			onBack: () => this.onBack()
		}

		this.chartViewObject.addListener('select:point', this.listeners.onSelectPoint)
		this.chartViewObject.addListener('map:zoomTo', features => {
			if (_.isEmpty(features)) {
				this.onBack()
			} else {
				let { treeId, zoomTo } = getDrilldownTreeIdAndZoomTo({
					chartDesigner: this.chartDesigner,
					mapKeys: features.map(f => f.id),
				})

				this.triggerDrilldown(treeId, zoomTo)
			}
		})
		this.chartViewObject.addListener('back', this.listeners.onBack)
	}

	onSelectPoint (point, range, opt) {
		if (_.isNil(point) || _.isNil(range)) return

		// tree-map charts only have 1 serie
		const plotType = this.chartDesigner.get('plotType')
		if (plotType === 'tree-map' || plotType === 'filled-map' ||
			plotType === 'spike-map' || plotType === 'bubble-map') {

			this.triggerDrilldown(range.id, opt?.zoomTo)
		} else {
			// #41471 For any non map types that support drilldowns, we
			// simply want to navigate to their data source
			const serieId = point.serieId
			const serie = this.chartDesigner.features.find(f => f.id == serieId || f.tempId == serieId)
			if (!serie) return

			this.trigger('goto', { serie })
		}
	}

	triggerDrilldown (treeId, zoomTo) {
		if (layoutManager.sharedResource) {
			// #60460 drilldown is disabled on shared dashboards
			return
		}

		let fieldIds = this.drilldownFieldIds()

		let filterIds
		if (_.isEmpty(zoomTo)) {
			filterIds = [treeId]
		} else {
			filterIds = zoomTo.map(item => item.id)
		}
		let drilldownFilters = this.buildDrilldownFilters(filterIds)

		this.trigger('drilldown', new DrilldownState(_.extend({
			drilldownFilters,
			drilldownId: treeId,
			zoomTo: zoomTo
		}, fieldIds)))
	}

	buildDrilldownFilters (treeIds) {
		let fieldIds = this.drilldownFieldIds()

		let applyDrilldownAction = applyDrilldownActionFactory({
			widgetView: this.chartWidgetView
		})

		let newFilter = _.extend({}, fieldIds)

		let drilldownFilters = applyFilter.rollupDrilldown({
			targetDrilldownFieldId: fieldIds.fieldId,
			filter: newFilter,
			// #44364 don't send existing filters into the applyFilter code.
			// We don't want to send serie filters, because it could wipe
			// out filters that we want to be kept. And there's no need to
			// send filters generated by a previous drilldown, because these
			// will just replace it
			filters: [],
			value: treeIds.map(id => parseInt(id))
		})

		drilldownFilters.forEach(applyDrilldownAction)

		return drilldownFilters
	}

	drilldownFieldIds () {
		// As of right now, any drilldowns are on tree maps and filled maps, which
		// only support a single serie
		let serie = this.chartDesigner.features.first()
		return {
			fieldId: serie.get('datasetRollupFieldId'),
			visibleLinkedFieldId: serie.get('datasetRollupVisibleLinkedFieldId')
		}
	}

	onBack () {
		// tree-map charts only have 1 serie
		let serie = this.chartDesigner.features.first()

		// if we are starting at a manually selected location, we need to
		// manually add in rollup filters
		const isFilledMap = this.chartDesigner.get('plotType') === 'filled-map'
		if (isFilledMap && _.isNil(this.chartDesigner.data.currentDrilldown())) {
			this.setInitialGeographicDrilldown()
		}

		// #50071 not using this.chartDesigner.data.rollupParentId() to get the
		// parentId, because when drilling down to a leaf node, fresh data isn't
		// fetched. Thus, the data is from the previous query, and the "parentId"
		// returned by it is actually the grandparent of what's currently displayed
		let parentId
		let currentDrilldown = this.chartDesigner.data.currentDrilldown()
		let currentDrilldownId = currentDrilldown?.drilldownId

		if (currentDrilldownId) {
			// if we were already zoomed (drilled down to a leaf node, or
			// several nodes selected), then the drilldown id is already
			// correct. We just need to rerender the chart after we delete the
			// zoomTo.
			// Note that we'll still need to trigger the back event so that
			// the filters get updated for the other dashboard widgets
			if (currentDrilldown.isZoomed()) parentId = currentDrilldownId
			else {
				let treeItem = this.chartDesigner.data.treeItems(
					currentDrilldownId)

				// #56598 Tree maps use the currentDrilldown info. The
				// parentId will be the rollupParentId on the chart serie.
				parentId = treeItem?.parentId ??
					this.chartDesigner.data.rollupParentId()
			}
		}

		if (isFilledMap) if (_.isNil(parentId)) return

		let underlyingField = null
		let datasetId = _.get(serie.get('source'), 'aggregation.startingDatasetId')
		let fieldIds = this.drilldownFieldIds()
		let fieldId = fieldIds.fieldId
		if (!!datasetId) {
			let dataset = this.chartDesigner.data.datasetModel(datasetId)
			underlyingField = dataset.getUnderlyingField(fieldId)
		}

		let backFilters = applyFilter.rollupBack({
			fieldId,
			underlyingField,
			filters: _.get(this.chartDesigner.data.currentDrilldown(), 'drilldownFilters'),
			parentId
		})

		let applyDrilldownAction = applyDrilldownActionFactory({
			widgetView: this.chartWidgetView
		})
		backFilters.forEach(applyDrilldownAction)

		this.trigger('back', new DrilldownState(_.extend({
			drilldownFilters: backFilters,
			drilldownId: parentId
		}, fieldIds)))
	}

	setInitialGeographicDrilldown () {
		const dataSerie = _.get(this.chartDesigner.data.plots(),
			'[0].series[0]')
		const filter = {
			fieldId: this.chartDesigner.get('rollupFieldId'),
			rollupIsAnyFilters: { [dataSerie.drilldownId]: 'item-rollups-and-descendents' },
			operator: 'is-any'
		}

		this.chartDesigner.data.currentDrilldown(new DrilldownState({
			drilldownId: dataSerie.drilldownId,
			drilldownFilters: [filter]
		}))
	}

	remove () {
		this.chartViewObject.removeListener('select:point', this.listeners.onSelectPoint)
		this.chartViewObject.removeListener('back', this.listeners.onBack)
	}
}

/**
 * @typedef DrilldownState
 *
 * @property {String} [drilldownId]
 *
 * @property {ApiDatasetFilter[]} [drilldownFilters]
 *
 * @property {Integer} [fieldId]
 *
 * @property {Integer} [visibleLinkedFieldId]
 *
 * @property {DatasetTree[]} [zoomTo] - dataset tree items to zoom to
 */
class DrilldownState {
	constructor({ drilldownId, drilldownFilters, fieldId, visibleLinkedFieldId, zoomTo }) {
		this.drilldownId = drilldownId
		this.drilldownFilters = drilldownFilters
		this.fieldId = fieldId
		this.visibleLinkedFieldId = visibleLinkedFieldId
		this.zoomTo = zoomTo
	}

	/**
	 * Returns whether the drilldown is a "zoomed" state (ie several items selected)
	 * @returns {boolean}
	 */
	isZoomed () {
		return !_.isEmpty(this.zoomTo)
	}

	/**
	 * Returns the IDs of the tree items in currentDrilldown().zoomTo. Will be
	 * null if there is no current drilldown or no zoomToIds
	 * @returns {unknown[] | undefined}
	 */
	zoomToIds () {
		return this.zoomTo?.map(item => item.id)
	}
}

function getDrilldownTreeIdAndZoomTo ({ chartDesigner, mapKeys }) {
	let zoomToItems = mapKeys.map(key => {
		const treeItems = _.values(chartDesigner.data.treeItems())
		const treeItem = _.find(treeItems, ti =>
			ti.key == key)

		if (_.isNil(treeItem) || _.isEmpty(treeItem.centroid)) return

		return treeItem
	}).filter(item => item != null)

	let leafNode = false
	if (zoomToItems.length == 1) {
		const mapLevels = chartDesigner.getEnabledMapLevels()
		let treeItem = zoomToItems[0]
		if (treeTypeToDataType(treeItem.type) == _.last(mapLevels)) {
			leafNode = true
		}
	}

	let treeId, zoomTo
	if (zoomToItems.length == 1 && !leafNode) {
		// If the chart code gave us a single non-leaf item, then
		// this is simple: drilldown to that tree item, with no
		// specific "zoomTo" settings
		treeId = zoomToItems[0].id
		zoomTo = null
	} else {
		// If there are multiple items, then we want the "drilldown"
		// to be on the parent (so we can see them all)
		//
		// And if there's only 1 item, but it's a leaf node, then
		// we want the drilldown to stay as the parent, so that
		// the color doesn't change
		treeId = zoomToItems[0].parentId
		zoomTo = zoomToItems
	}

	return { treeId, zoomTo }
}

_.extend(DrilldownManager.prototype, Backbone.Events)

_.extend(DrilldownManager, {
	DrilldownState,
	getDrilldownTreeIdAndZoomTo
})

export default DrilldownManager
export { DrilldownState }
export { getDrilldownTreeIdAndZoomTo }


DrilldownManager.__filename__ = 'charts/drilldown-manager.js'
